import React, { useRef, useEffect, useState } from 'react';
import { X, CheckCircle2 } from 'lucide-react';

interface SharePointConnectGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface GuideTab {
  key: string;
  label: string;
  title: string;
  subSteps: string[];
}

const tabs: GuideTab[] = [
  {
    key: 'list',
    label: 'SharePoint List',
    title: 'Grant access to a SharePoint List',
    subSteps: [
      'Navigate to the SharePoint site that contains the list you want to connect.',
      'Open the list you want to share access to.',
      'Click the gear icon (Settings) in the top-right corner of the page.',
      'Select "List settings" from the dropdown menu.',
      'Under the "Permissions and Management" section, click "Permissions for this list".',
      'If the list inherits permissions, click "Stop Inheriting Permissions" first to manage them independently.',
      'Click "Grant Permissions" in the toolbar.',
      'In the Share dialog, enter the system user ID and select at least "Read" permission level.',
      'Click "Share" to confirm.',
    ],
  },
  {
    key: 'doc_library',
    label: 'Document Library',
    title: 'Grant access to an entire Document Library',
    subSteps: [
      'Navigate to the Document Library you want to share.',
      'Click the gear icon (Settings) in the top-right corner of the page.',
      'Select "Library settings" from the dropdown menu.',
      'Under the "Permissions and Management" section, click "Permissions for this document library".',
      'If the library inherits permissions, click "Stop Inheriting Permissions" first to manage them independently.',
      'Click "Grant Permissions" in the toolbar.',
      'In the Share dialog, enter the system user ID and select at least "Read" permission level.',
      'Click "Share" to confirm.',
      'This grants access to all folders and files within the entire library.',
    ],
  },
  {
    key: 'doc_library_folder',
    label: 'Library Folder',
    title: 'Grant access to a Document Library Folder',
    subSteps: [
      'Navigate to the Document Library that contains the folder you want to share.',
      'Locate the specific folder you want to grant access to.',
      'Hover over the folder and click the three dots (...) or right-click the folder name.',
      'Select "Manage access" from the context menu.',
      'In the Manage Access panel, click the "+" (Add) button or "Grant Access".',
      'Enter the system user ID in the search field.',
      'Set the permission to "Can view" (Read Only).',
      'Click "Grant Access" to confirm.',
    ],
  },
];

const SharePointConnectGuideModal: React.FC<SharePointConnectGuideModalProps> = ({
  isOpen,
  onClose,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (isOpen) {
      setActiveIndex(0);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const currentTab = tabs[activeIndex];

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div
        ref={modalRef}
        className="relative bg-white rounded-lg shadow-lg w-full max-w-[52rem] mx-4 flex flex-col"
        style={{ maxHeight: '85vh' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-4 pb-2">
          <h2 className="text-lg font-semibold text-gray-800">How to Grant Access in SharePoint</h2>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-black focus:outline-none"
            aria-label="Close"
            title="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 px-6">
          <div className="flex">
            {tabs.map((tab, index) => {
              const isActive = activeIndex === index;
              return (
                <button
                  key={tab.key}
                  className={`px-4 py-2 border-b-2 font-medium transition-colors text-sm ${
                    isActive
                      ? 'border-black text-black'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setActiveIndex(index)}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <h3 className="text-base font-semibold mb-4">{currentTab.title}</h3>

          {/* Numbered sub-steps */}
          <ol className="space-y-3 mb-6">
            {currentTab.subSteps.map((subStep, idx) => (
              <li key={idx} className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center text-xs font-semibold">
                  {idx + 1}
                </span>
                <p className="text-sm text-gray-700 leading-relaxed">{subStep}</p>
              </li>
            ))}
          </ol>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-6 py-3 border-t border-gray-200">
          <button
            onClick={onClose}
            className="bg-black text-white px-6 py-2 rounded-md text-sm font-medium hover:bg-gray-800 transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

export default SharePointConnectGuideModal;
